import { useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { TChatMessage } from "../../../../@types";
import { apis, callApi } from "../../../../api";
import { splitStringIntoChunks } from "../../../../utils";
import { useTeaching } from "./useTeaching";

export type TTeachingMessageService = {
	messages: TChatMessage[];
	listeners: (() => void)[];

	addEventListener: (cb: () => void) => void;
	removeEventListener: (cb: () => void) => void;
	emitChanges: () => void;
	clearData: () => void;

	setMessages: (messages: TChatMessage[]) => void;
	sendMessage: (message: TChatMessage) => void;
};

export const TeachingMessageService: TTeachingMessageService = {
	messages: [],
	listeners: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	clearData() {
		this.messages = [];
		this.emitChanges();
	},

	setMessages(messages) {
		this.messages = messages;
	},

	sendMessage(message) {
		this.messages = [message, ...this.messages];
	},
};

export function useTeachingMessageInit() {
	const { student } = useTeaching();
	const [searchParams] = useSearchParams();
	const tab = searchParams.get("tab") ?? "";
	const isMessage = tab === "message";

	useEffect(() => {
		const getMessages = async () => {
			try {
				if (!student || !isMessage) {
					return;
				}
				const response = await callApi({
					method: "GET",
					url: `${apis.MESSAGES_BY_USERS}/${student.userId}`,
				});

				if (!response) {
					return;
				}

				const { messages } = response;

				if (messages.length > 0) {
					TeachingMessageService.setMessages(messages.reverse());
				}
			} catch (error) {
			} finally {
				TeachingMessageService.emitChanges();
			}
		};

		getMessages();
	}, [isMessage, student]);

	useEffect(() => {
		return () => {
			TeachingMessageService.clearData();
		};
	}, []);
}

export function useTeachingMessage() {
	const [messages, setMessages] = useState(TeachingMessageService.messages);

	useEffect(() => {
		const updateData = () => {
			setMessages(TeachingMessageService.messages);
		};

		TeachingMessageService.addEventListener(updateData);

		return () => {
			TeachingMessageService.removeEventListener(updateData);
		};
	}, []);

	const splitMessageAndSend = async (data: {
		receiverId: string;
		body: string;
	}) => {
		const splittedMessages = splitStringIntoChunks(data.body, 1000);
		for (let i = 0; i < splittedMessages.length; i++) {
			await sendMessage({
				receiverId: data.receiverId,
				body: splittedMessages[i].trim(),
			});
		}
	};

	const sendMessage = async (data: { receiverId: string; body: string }) => {
		try {
			const response = await callApi({
				method: "POST",
				url: apis.MESSAGES_CHAT,
				body: data,
			});

			if (!response) {
				return;
			}

			const message = response.chat;
			TeachingMessageService.sendMessage(message);
			TeachingMessageService.emitChanges();
		} catch (error) {}
	};

	return { messages, splitMessageAndSend };
}
